import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Activity, MapPin, Clock, User, Calendar, Filter,
  ChevronDown, ChevronUp, Eye, Navigation, Route,
  Search, Download, RefreshCw, Target, Map, Layers,
  CheckCircle, AlertCircle, FileText, Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const EnhancedActivityMaps = ({ onClose }) => {
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [activityHistory, setActivityHistory] = useState([]);
  const [locationHistory, setLocationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [dateRange, setDateRange] = useState('today');
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [showMap, setShowMap] = useState(true);

  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem('field_staff_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    if (selectedStaff) {
      console.log('Fetching data for staff:', selectedStaff.id, selectedStaff.name);
      setLoadingData(true);
      Promise.all([
        fetchActivityHistory(selectedStaff.id),
        fetchLocationHistory(selectedStaff.id)
      ]).finally(() => {
        setLoadingData(false);
      });
    } else {
      // Clear data when no staff selected
      setActivityHistory([]);
      setLocationHistory([]);
    }
  }, [selectedStaff?.id, dateRange]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const userData = localStorage.getItem('field_staff_user');
      const parsedUser = userData ? JSON.parse(userData) : null;
      
      // Fetch staff members
      const staffResponse = await axios.get(
        `${API_URL}/api/field-staff/admin/staff`,
        getAuthConfig()
      );
      const staffData = (staffResponse.data || []).map(s => ({ ...s, userType: 'staff' }));
      
      // Fetch admins if user is super_admin
      let adminData = [];
      if (parsedUser?.role === 'super_admin') {
        try {
          const adminResponse = await axios.get(
            `${API_URL}/api/field-staff/super-admin/admins`,
            getAuthConfig()
          );
          adminData = (adminResponse.data || []).map(a => ({ 
            ...a, 
            userType: a.role === 'super_admin' ? 'super_admin' : 'admin',
            position: a.role === 'super_admin' ? 'Super Admin' : 'Admin'
          }));
        } catch (adminError) {
          console.error('Error fetching admins:', adminError);
        }
      }
      
      // Combine staff and admins
      const allUsers = [...adminData, ...staffData];
      console.log('Users loaded:', staffData.length, 'staff,', adminData.length, 'admins');
      setStaff(allUsers);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to fetch user list');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityHistory = async (staffId) => {
    try {
      console.log('Fetching activity history for:', staffId);
      const response = await axios.get(
        `${API_URL}/api/field-staff/admin/activity-logs?user_id=${staffId}&limit=100`,
        getAuthConfig()
      );
      const activities = response.data || [];
      console.log('Activity history loaded:', activities.length, 'records');
      setActivityHistory(activities);
    } catch (error) {
      console.error('Error fetching activity history:', error);
      toast.error('Failed to load activity history');
      setActivityHistory([]);
    }
  };

  const fetchLocationHistory = async (staffId) => {
    try {
      console.log('Fetching location history for:', staffId);
      const response = await axios.get(
        `${API_URL}/api/field-staff/admin/staff/${staffId}/location-history?limit=200`,
        getAuthConfig()
      );
      const locations = response.data || [];
      console.log('Location history loaded:', locations.length, 'points');
      setLocationHistory(locations);
    } catch (error) {
      console.error('Error fetching location history:', error);
      setLocationHistory([]);
    }
  };

  const handleStaffSelect = (member) => {
    console.log('Staff selected:', member.id, member.name);
    setSelectedStaff(member);
    setExpandedActivity(null);
    setActivityFilter('all');
  };

  const getActivityIcon = (action) => {
    const icons = {
      login: '🔑',
      logout: '🚪',
      task_started: '▶️',
      task_completed: '✅',
      completed_task: '✅',
      verified_task: '☑️',
      created_task: '📋',
      updated_task: '✏️',
      location_update: '📍',
      added_task_update: '📝',
      submitted_payment: '💰',
      verified_task_completion: '✔️'
    };
    return icons[action] || '📋';
  };

  const getActivityColor = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-700 border-green-200',
      logout: 'bg-gray-100 text-gray-700 border-gray-200',
      task_completed: 'bg-blue-100 text-blue-700 border-blue-200',
      completed_task: 'bg-blue-100 text-blue-700 border-blue-200',
      task_started: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      created_task: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      submitted_payment: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      verified_task_completion: 'bg-teal-100 text-teal-700 border-teal-200'
    };
    return colors[action] || 'bg-purple-100 text-purple-700 border-purple-200';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Filter activities
  const filteredActivities = activityHistory.filter(activity => {
    if (activityFilter !== 'all' && activity.action !== activityFilter) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        activity.action?.toLowerCase().includes(searchLower) ||
        activity.resource_type?.toLowerCase().includes(searchLower) ||
        JSON.stringify(activity.details || {}).toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Get unique activity types
  const activityTypes = [...new Set(activityHistory.map(a => a.action))];

  // Calculate route path for visualization
  const getRoutePath = () => {
    if (locationHistory.length < 2) return null;
    
    // Normalize coordinates for SVG
    const locs = locationHistory.slice(0, 50).reverse(); // Show last 50 points, oldest first
    const lats = locs.map(l => l.location?.latitude || l.latitude);
    const lngs = locs.map(l => l.location?.longitude || l.longitude);
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    const latRange = maxLat - minLat || 0.001;
    const lngRange = maxLng - minLng || 0.001;
    
    const points = locs.map(loc => {
      const lat = loc.location?.latitude || loc.latitude;
      const lng = loc.location?.longitude || loc.longitude;
      return {
        x: ((lng - minLng) / lngRange) * 280 + 10,
        y: ((maxLat - lat) / latRange) * 180 + 10
      };
    });
    
    return points;
  };

  const routePoints = getRoutePath();

  // Calculate stats
  const stats = {
    totalActivities: activityHistory.length,
    tasksCompleted: activityHistory.filter(a => 
      a.action === 'task_completed' || a.action === 'completed_task'
    ).length,
    locationPoints: locationHistory.length,
    paymentsSubmitted: activityHistory.filter(a => a.action === 'submitted_payment').length
  };

  // Filter staff by search
  const filteredStaff = staff.filter(member => 
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.position?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-xl overflow-hidden" data-testid="activity-maps">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Activity & Route History</h2>
              <p className="text-purple-100 text-sm">
                Track staff and admin activities
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowMap(!showMap)}
              className="text-white hover:bg-white/20"
            >
              <Layers className="w-4 h-4 mr-1" />
              {showMap ? 'Hide' : 'Show'} Map
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
                ✕
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex min-h-[600px]">
        {/* Staff Selection Sidebar */}
        <div className="w-72 border-r bg-gray-50 overflow-y-auto">
          <div className="p-3 bg-white border-b sticky top-0 z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search staff & admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>
          
          <div className="p-2 space-y-2">
            {loading ? (
              <div className="text-center py-8">
                <Activity className="w-8 h-8 animate-spin mx-auto text-purple-500" />
                <p className="text-sm text-gray-500 mt-2">Loading...</p>
              </div>
            ) : filteredStaff.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p className="font-medium">No users found</p>
              </div>
            ) : (
              filteredStaff.map((member) => (
                <div
                  key={member.id}
                  onClick={() => handleStaffSelect(member)}
                  className={`p-3 bg-white rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    selectedStaff?.id === member.id 
                      ? 'border-purple-500 shadow-md ring-2 ring-purple-100' 
                      : 'border-gray-200'
                  }`}
                  data-testid={`staff-item-${member.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                      member.userType === 'super_admin' ? 'bg-gradient-to-br from-purple-600 to-indigo-700' :
                      member.userType === 'admin' ? 'bg-gradient-to-br from-blue-500 to-cyan-600' :
                      member.is_active ? 'bg-purple-500' : 'bg-gray-400'
                    }`}>
                      {member.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{member.name}</h4>
                      <div className="flex items-center gap-1">
                        <p className="text-xs text-gray-500">{member.position || 'Field Staff'}</p>
                        {member.userType === 'super_admin' && (
                          <Badge className="text-[9px] bg-purple-100 text-purple-700 px-1 py-0">SA</Badge>
                        )}
                        {member.userType === 'admin' && (
                          <Badge className="text-[9px] bg-blue-100 text-blue-700 px-1 py-0">Admin</Badge>
                        )}
                      </div>
                    </div>
                    <Badge variant={member.is_active !== false ? 'default' : 'secondary'} className="text-xs">
                      {member.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {!selectedStaff ? (
            <div className="h-full flex items-center justify-center text-gray-500 p-8">
              <div className="text-center">
                <Route className="w-20 h-20 mx-auto mb-4 opacity-20" />
                <h3 className="text-xl font-medium text-gray-700">Select a User</h3>
                <p className="text-sm mt-2">Choose a staff member or admin to view their activity and location history</p>
              </div>
            </div>
          ) : loadingData ? (
            <div className="h-full flex items-center justify-center text-gray-500 p-8">
              <div className="text-center">
                <Activity className="w-12 h-12 mx-auto mb-4 animate-spin text-purple-500" />
                <h3 className="text-lg font-medium text-gray-700">Loading Data...</h3>
                <p className="text-sm mt-2">Fetching activity and location history for {selectedStaff.name}</p>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Staff Info Header */}
              <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                    <User className="w-8 h-8 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedStaff.name}</h3>
                    <p className="text-sm text-gray-500">
                      {selectedStaff.position || 'Field Staff'} • {selectedStaff.area_covered || 'No area assigned'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{selectedStaff.email}</p>
                  </div>
                </div>
                
                {/* Date Range Filter */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <select
                      value={dateRange}
                      onChange={(e) => setDateRange(e.target.value)}
                      className="text-sm focus:outline-none bg-transparent"
                    >
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="all">All Time</option>
                    </select>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      fetchActivityHistory(selectedStaff.id);
                      fetchLocationHistory(selectedStaff.id);
                    }}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-4 gap-3">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <Activity className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-blue-700">{stats.totalActivities}</p>
                    <p className="text-xs text-blue-600">Total Activities</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-green-700">{stats.tasksCompleted}</p>
                    <p className="text-xs text-green-600">Tasks Completed</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <MapPin className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-purple-700">{stats.locationPoints}</p>
                    <p className="text-xs text-purple-600">Location Points</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <Target className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-emerald-700">{stats.paymentsSubmitted}</p>
                    <p className="text-xs text-emerald-600">Payments</p>
                  </CardContent>
                </Card>
              </div>

              {/* Activity Timeline & Location Map */}
              <div className={`grid ${showMap ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                {/* Activity Timeline */}
                <Card className={showMap ? '' : 'col-span-1'}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Activity Timeline
                      </CardTitle>
                      <select
                        value={activityFilter}
                        onChange={(e) => setActivityFilter(e.target.value)}
                        className="text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        <option value="all">All Types</option>
                        {activityTypes.map(type => (
                          <option key={type} value={type}>{type?.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                  </CardHeader>
                  <CardContent className="max-h-80 overflow-y-auto">
                    {filteredActivities.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">
                        <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        No activities recorded
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {filteredActivities.slice(0, 30).map((activity, idx) => (
                          <div
                            key={activity.id || idx}
                            className={`p-3 rounded-lg border transition-all ${
                              expandedActivity === idx ? 'bg-gray-50 border-purple-200' : 'hover:bg-gray-50'
                            } ${getActivityColor(activity.action)}`}
                          >
                            <div 
                              className="flex items-center gap-3 cursor-pointer"
                              onClick={() => setExpandedActivity(expandedActivity === idx ? null : idx)}
                            >
                              <span className="text-xl">{getActivityIcon(activity.action)}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium capitalize truncate">
                                  {activity.action?.replace(/_/g, ' ')}
                                </p>
                                <p className="text-xs text-gray-500">{formatTime(activity.timestamp)}</p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {activity.resource_type}
                              </Badge>
                              {expandedActivity === idx ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            {expandedActivity === idx && activity.details && (
                              <div className="mt-3 pt-3 border-t text-xs bg-white rounded p-2 overflow-x-auto">
                                <pre className="text-gray-600 whitespace-pre-wrap">
                                  {JSON.stringify(activity.details, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Location History Map */}
                {showMap && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Map className="w-4 h-4" /> Route History
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="h-80 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg relative overflow-hidden">
                      {locationHistory.length > 0 && routePoints ? (
                        <svg className="w-full h-full" viewBox="0 0 300 200">
                          {/* Grid */}
                          <defs>
                            <pattern id="routeGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill="url(#routeGrid)" />
                          
                          {/* Route path */}
                          <path
                            d={`M ${routePoints.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                            fill="none"
                            stroke="#8B5CF6"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          
                          {/* Animated dash */}
                          <path
                            d={`M ${routePoints.map(p => `${p.x} ${p.y}`).join(' L ')}`}
                            fill="none"
                            stroke="#C4B5FD"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray="5,10"
                            className="animate-pulse"
                          />
                          
                          {/* Location points */}
                          {routePoints.map((point, idx) => {
                            const isFirst = idx === 0;
                            const isLast = idx === routePoints.length - 1;
                            const showPoint = isFirst || isLast || idx % 5 === 0;
                            
                            if (!showPoint) return null;
                            
                            return (
                              <g key={idx}>
                                <circle
                                  cx={point.x}
                                  cy={point.y}
                                  r={isFirst || isLast ? 6 : 3}
                                  fill={isFirst ? '#22C55E' : isLast ? '#EF4444' : '#8B5CF6'}
                                  stroke="white"
                                  strokeWidth="2"
                                />
                                {isFirst && (
                                  <text x={point.x + 8} y={point.y + 4} fontSize="10" fill="#22C55E" fontWeight="bold">
                                    Start
                                  </text>
                                )}
                                {isLast && (
                                  <text x={point.x + 8} y={point.y + 4} fontSize="10" fill="#EF4444" fontWeight="bold">
                                    Current
                                  </text>
                                )}
                              </g>
                            );
                          })}
                        </svg>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                          <Navigation className="w-16 h-16 mb-3 opacity-20" />
                          <p className="font-medium">No location history</p>
                          <p className="text-xs">Location data will appear here</p>
                        </div>
                      )}
                      
                      {/* Legend */}
                      <div className="absolute bottom-2 left-2 flex items-center gap-4 text-xs bg-white/90 rounded-lg px-3 py-2 shadow">
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div> Start
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-purple-500"></div> Route
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div> Current
                        </span>
                      </div>
                      
                      {/* Stats badge */}
                      <div className="absolute top-2 right-2 bg-white/90 rounded-lg px-3 py-1 shadow text-xs">
                        {locationHistory.length} points tracked
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedActivityMaps;
